import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Plus, X } from 'lucide-react-native';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Keyboard, Platform, useWindowDimensions } from 'react-native';
import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';

import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetScrollView,
} from '@/components/ui/actionsheet';
import { Box } from '@/components/ui/box';
import { Button, ButtonIcon, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Input, InputField, InputIcon, InputSlot } from '@/components/ui/input';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import {
  ADD_REFERENCES_SESSION_STATUS,
  type AddReferencesStore,
} from '@/services/stores/addReferencesStore';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { SMALL_ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';

type AddReferencesUrlSheetProps = {
  store: StoreApi<AddReferencesStore>;
  isOpen: boolean;
  onClose: () => void;
  /** Parent reads the current URLs from the store and runs the format-and-send
   * flow — no args needed here. */
  onSubmit: () => void;
};

const URL_VALIDATION_REGEX = /^https?:\/\/\S+$/i;

function hasValidUrl(urls: string[]): boolean {
  return urls.some((url) => URL_VALIDATION_REGEX.test(url.trim()));
}

/** True when any non-empty row fails the regex. Empty rows are ignored. */
function hasAnyInvalidUrl(urls: string[]): boolean {
  return urls.some((url) => {
    const trimmed = url.trim();

    return trimmed.length > 0 && !URL_VALIDATION_REGEX.test(trimmed);
  });
}

/**
 * Internal content — shared between the Modal (web) and Actionsheet (native)
 * presentations below. All state lives in the Zustand store so this component
 * stays stateless.
 */
function UrlEntryContent({
  store,
  onSubmit,
  onClose,
}: {
  store: StoreApi<AddReferencesStore>;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const urls = useStore(store, (s) => s.urls);

  const status = useStore(store, (s) => s.status);

  const errorMessage = useStore(store, (s) => s.errorMessage);

  const setUrlAt = useStore(store, (s) => s.setUrlAt);

  const addUrlRow = useStore(store, (s) => s.addUrlRow);

  const removeUrlAt = useStore(store, (s) => s.removeUrlAt);

  const isFormatting = status === ADD_REFERENCES_SESSION_STATUS.FORMATTING;

  const canSubmit = useMemo(
    () => hasValidUrl(urls) && !hasAnyInvalidUrl(urls) && !isFormatting,
    [urls, isFormatting]
  );

  const handleSend = useCallback(() => {
    if (!canSubmit) {
      return;
    }

    onSubmit();
  }, [canSubmit, onSubmit]);

  return (
    <VStack space="lg" className="w-full">
      <Text className="text-sm text-typography-700">
        Paste one URL per field. We'll format each reference and post it to the conversation.
      </Text>
      <VStack space="sm" className="w-full">
        {urls.map((url, index) => {
          const isOnlyRow = urls.length === 1;

          const trimmed = url.trim();

          const isInvalidUrl = trimmed.length > 0 && !URL_VALIDATION_REGEX.test(trimmed);

          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: URLs are reordered by index and can be empty; index is stable per row.
            <VStack key={index} space="xs">
              <Input size="md" isDisabled={isFormatting} isInvalid={isInvalidUrl}>
                <InputField
                  placeholder="https://..."
                  value={url}
                  onChangeText={(next) => setUrlAt(index, next)}
                  autoCapitalize="none"
                  autoCorrect={false}
                  keyboardType="url"
                  multiline={false}
                  numberOfLines={1}
                  scrollEnabled
                  style={{ height: '100%', textAlignVertical: 'center' }}
                  {...getAccessibilityProps({
                    label: `URL ${index + 1}`,
                  })}
                />
                {!isOnlyRow ? (
                  <InputSlot
                    className="min-w-[56px] items-center justify-center pr-3"
                    hitSlop={SMALL_ICON_BUTTON_HIT_SLOP}
                    onPress={() => removeUrlAt(index)}
                    disabled={isFormatting}
                    {...getAccessibilityProps({
                      label: `Remove URL ${index + 1}`,
                      role: 'button',
                    })}>
                    <InputIcon as={X} className="text-typography-500" />
                  </InputSlot>
                ) : null}
              </Input>
              {isInvalidUrl ? (
                <Text size="xs" className="text-error-600">
                  URL must start with http:// or https://
                </Text>
              ) : null}
            </VStack>
          );
        })}
      </VStack>
      <Button
        variant="link"
        size="sm"
        onPress={addUrlRow}
        disabled={isFormatting}
        className="self-start">
        <ButtonIcon as={Plus} size="sm" />
        <ButtonText>Add another</ButtonText>
      </Button>
      {errorMessage ? <Text className="text-sm text-error-600">{errorMessage}</Text> : null}
      <HStack space="sm">
        <Button
          variant="outline"
          size="md"
          onPress={onClose}
          disabled={isFormatting}
          className="flex-1">
          <ButtonText>Cancel</ButtonText>
        </Button>
        <Button size="md" onPress={handleSend} disabled={!canSubmit} className="flex-1">
          {isFormatting ? <Spinner size="small" /> : null}
          <ButtonText numberOfLines={1}>{isFormatting ? 'Sending…' : 'Send'}</ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
}

/**
 * Action sheet for the proactive "Add references" toolbar button. Renders as a
 * Modal on web (mobile + desktop) and a bottom-sheet Actionsheet on native —
 * matching the convention in `InquiryGuidelines.tsx`.
 *
 * The parent wires `onSubmit` to a flow that calls the format-references-batch
 * endpoint then posts each reference as an individual `reference`-type message.
 */
export const AddReferencesUrlSheet = memo(function AddReferencesUrlSheet({
  store,
  isOpen,
  onClose,
  onSubmit,
}: AddReferencesUrlSheetProps) {
  if (isWeb) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalBackdrop />
        <ModalContent>
          <ModalHeader>
            <Heading size="md">Provide references</Heading>
          </ModalHeader>
          <ModalBody>
            <UrlEntryContent store={store} onSubmit={onSubmit} onClose={onClose} />
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>
    );
  }

  return <NativeUrlSheet store={store} isOpen={isOpen} onClose={onClose} onSubmit={onSubmit} />;
});

const isAndroid = Platform.OS === 'android';

/**
 * Native (Actionsheet) presentation.
 *
 * Sheet sizing:
 * - **iOS**: `height: sheetMaxHeight` so the sheet consistently fills ~92%
 *   of the screen even when content is short.
 * - **Android**: `maxHeight: sheetMaxHeight` so the sheet sizes to content
 *   up to the cap. Forcing `height` on Android fights the bottom-sheet snap
 *   behavior and leaves parts of a long sheet un-scrollable past the
 *   visible viewport.
 *
 * Keyboard handling (both platforms):
 * - Listen for keyboard events (iOS uses `Will*`, Android uses `Did*`) and
 *   add `paddingBottom = keyboardHeight + gutter` to the ScrollView's
 *   content so the Cancel/Send CTAs + every URL input can be scrolled
 *   above the soft keyboard rather than sitting behind it. Android's
 *   `adjustResize` does not lift a Gluestack Actionsheet automatically, so
 *   we need the manual padding there too.
 *
 * Using `useWindowDimensions` so the height cap tracks viewport resize
 * rather than a stale mount-time value.
 */
function NativeUrlSheet({ store, isOpen, onClose, onSubmit }: AddReferencesUrlSheetProps) {
  const { height: windowHeight } = useWindowDimensions();

  const sheetMaxHeight = Math.round(windowHeight * 0.92);

  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    const showEvent = isAndroid ? 'keyboardDidShow' : 'keyboardWillShow';

    const hideEvent = isAndroid ? 'keyboardDidHide' : 'keyboardWillHide';

    const showSub = Keyboard.addListener(showEvent, (event) => {
      setKeyboardHeight(event.endCoordinates.height);
    });

    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0);
    });

    return () => {
      showSub.remove();

      hideSub.remove();
    };
  }, []);

  return (
    <Actionsheet isOpen={isOpen} onClose={onClose}>
      <ActionsheetBackdrop />
      <ActionsheetContent
        className="w-full rounded-t-2xl p-0"
        style={isAndroid ? { maxHeight: sheetMaxHeight } : { height: sheetMaxHeight }}>
        <ActionsheetDragIndicatorWrapper>
          <ActionsheetDragIndicator />
        </ActionsheetDragIndicatorWrapper>
        <Box className="w-full px-6 pt-4">
          <Heading size="md">Provide references</Heading>
        </Box>
        <ActionsheetScrollView
          contentContainerClassName="px-6 py-4"
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ paddingBottom: 40 + keyboardHeight }}>
          <UrlEntryContent store={store} onSubmit={onSubmit} onClose={onClose} />
        </ActionsheetScrollView>
      </ActionsheetContent>
    </Actionsheet>
  );
}
